import { useReportingContext } from '@views/pages/Reporting/Reports/context';
import { ReportingTableEntry } from '@views/pages/Reporting/Reports/hooks/useReportingTable.tsx';
import { useState } from 'react';
import {
  useDeleteReportFavoriteMutation,
  useDeleteReportMutation,
  useMakeReportFavoriteMutation,
  useReportsQuery,
} from '@common/cache';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { ReportingPageTableProps } from '@views/pages/Reporting/Reports/components';

export const useReportingPageTable = ({ query }: ReportingPageTableProps) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [reportIdToDelete, setReportIdToDelete] = useState('');
  const [reportIdToFavorite, setReportIdToFavorite] = useState('');
  const { filters, handleFilterChange, isInitialFilters } =
    useReportingContext();

  const {
    data = {
      reports: [],
      total: 0,
    },
    isPending,
  } = useReportsQuery({
    ...filters,
    ...(query === 'favorites_only' && { favorites_only: true }),
    ...(query === 'created_by_me' && { created_by_me: true }),
    ...(query === 'recently_deleted' && { recently_deleted: true }),
    ...(query === 'recently_viewed' && { recently_viewed: true }),
  });

  const {
    mutateAsync: deleteReportMutation,
    isPending: deleteReportIsLoading,
  } = useDeleteReportMutation();

  const {
    mutateAsync: makeReportFavoriteMutation,
    isPending: makeReportFavoriteIsPending,
  } = useMakeReportFavoriteMutation();

  const {
    mutateAsync: deleteReportFavoriteMutation,
    isPending: deleteReportFavoriteIsPending,
  } = useDeleteReportFavoriteMutation();

  const handleOpenDeleteModal = (id: string) => {
    setIsDeleteModalOpen(true);
    setReportIdToDelete(id);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setReportIdToDelete('');
  };

  const handleDeleteReport = async () => {
    try {
      await deleteReportMutation(reportIdToDelete);

      toast.success('Report deleted successfully.');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      handleCloseDeleteModal();
    }
  };

  const handleMakeReportFavorite = async (id: string, isFavorite: boolean) => {
    setReportIdToFavorite(id);
    const action = isFavorite
      ? deleteReportFavoriteMutation
      : makeReportFavoriteMutation;

    try {
      await action(id);

      toast.success(
        isFavorite
          ? 'Report deleted from favorite successfully'
          : 'Report set as favorite successfully.',
      );
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setReportIdToFavorite('');
    }
  };

  const entries: ReportingTableEntry[] = data.reports.map(item => {
    return {
      ...item,
      created_by: {
        id: item.created_by?.id || '-',
        name: item.created_by?.name || '-',
      },
      folder: {
        id: item.folder.id,
        name: item.folder.name,
        organization: {
          id: item?.folder?.organization?.id || '',
          name: item?.folder?.organization?.name || '',
        },
      },
    };
  });

  return {
    filters,
    entries,
    handleFilterChange,
    totalCount: data.total,
    loading: isPending,
    isDeleteModalOpen,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    handleDeleteReport,
    deleteReportIsLoading,
    makeReportFavoriteIsPending:
      makeReportFavoriteIsPending || deleteReportFavoriteIsPending,
    handleMakeReportFavorite,
    reportIdToFavorite,
    isInitialFilters,
  };
};
